import React from "react";
import "../styles/service.css";
import Navbar from "../components/layout/Navbar";
import SeoHead from "../components/SeoHead";

const CONDITIONS = [
  {
    title: "Brachial Plexus Tumours",
    desc: "Expert diagnosis and surgical removal of tumours affecting the brachial plexus.",
  },
  {
    title: "Obstetric Brachial Plexus Injuries",
    desc: "Reconstructive care for nerve injuries sustained during childbirth.",
  },
  {
    title: "Traumatic Brachial Plexus Injuries",
    desc: "Treatment for partial and complete nerve injuries from accidents or trauma.",
  },
  {
    title: "Free Functional Muscle Transfer",
    desc: "Advanced microsurgery transplanting muscle to restore lost arm function.",
  },
];

const GALLERY = [
  {
    src: "/images/peripheral&brachial1.jpeg",
    alt: "Peripheral & Brachial Surgery 1",
  },
  {
    src: "/images/peripheral&brachial2.jpeg",
    alt: "Peripheral & Brachial Surgery 2",
  },
  {
    src: "/images/peripheral&brachial3.jpeg",
    alt: "Peripheral & Brachial Surgery 3",
  },
];

export default function BranchialSurgery() {
  return (
    <div className="hs-page">

      <SeoHead
        title="Brachial Plexus Surgery Hyderabad | Dr. R. Suneel"
        description="Complex brachial plexus reconstruction, nerve transfers and free functional muscle transfer by Dr. R. Suneel. Advanced microsurgical care in Hyderabad."
        keywords="brachial plexus surgery Hyderabad, brachial plexus surgeon Hyderabad, brachial plexus injury treatment Hyderabad, traumatic brachial plexus injury, obstetric brachial plexus injury, brachial plexus nerve repair, brachial plexus reconstruction surgery, nerve graft surgery Hyderabad, brachial plexus specialist Hyderabad, microsurgery for brachial plexus injuries, Dr R Suneel brachial plexus surgeon, Medicover brachial plexus surgery Hyderabad"
        url="https://www.handandwristclinics.com/branchial"
        image="https://www.handandwristclinics.com/static/media/branchial_surgery.png"
      />

      {/* HERO + BREADCRUMB */}
      <header className="hs-hero">
        <div className="hs-hero-inner">
          <nav className="hs-breadcrumb" aria-label="Breadcrumb">
            <a href="/">Home</a>
            <span className="hs-bc-sep">/</span>
            <a href="/services">Services</a>
            <span className="hs-bc-sep">/</span>
            <span className="hs-bc-current">Brachial Surgery</span>
          </nav>
          <div className="hs-eyebrow">
            <span></span> Specialized Hand & Wrist Care
          </div>
          <h1 className="hs-title">
            Brachial Plexus <em>Surgery</em>
          </h1>
          <div className="hs-divider" />
          <p className="hs-hero-sub">
            Advanced surgical treatment to restore function, strength, and mobility
            to injured or impaired hands.
          </p>
        </div>
      </header>

      {/* INTRO */}
      <section className="simple-content">
  <div className="container">

    <h2>Brachial Plexus Surgery</h2>
    <p>
      The brachial plexus is a network of nerves that provides movement and sensation 
      to the shoulder, arm, and hand. These nerves originate from the spinal cord in 
      the neck region and control various functions of the upper limb.
    </p>

    <h3>Causes of Brachial Plexus Injury</h3>
    <p>
      Brachial plexus injuries can occur due to birth-related trauma (Obstetric Brachial Plexus Injury) 
      during delivery, which may lead to partial or complete loss of movement or sensation in the affected arm.
    </p>
    <p>
      Traumatic injuries are also common and may result from road accidents, sports injuries, or falls. 
      These injuries can range from mild stretching of the nerve to severe cases where the nerve root 
      is torn from the spinal cord.
    </p>

    <h3>Types of Brachial Plexus Conditions</h3>
    <ul>
      <li>Brachial Plexus Tumors</li>
      <li>Obstetric Brachial Plexus Injuries (OBPP)</li>
      <li>Traumatic Brachial Plexus Injuries (Partial / Complete)</li>
      <li>Free Functional Muscle Transfer</li>
    </ul>

    <h3>Surgical Treatment</h3>
    <p>
      The timing of surgery plays a crucial role in recovery. Muscles that remain disconnected 
      from nerves for a prolonged period may weaken and lose the ability to recover.
    </p>
    <p>
      In severe cases such as nerve avulsion or rupture, timely surgical intervention is essential 
      for optimal recovery. For milder conditions like neurapraxia, recovery rates can be high, 
      often reaching 95–100% with proper care.
    </p>
    <p>
      When required, microsurgical nerve repair may be performed as early as 3 months after injury. 
      In infants, primary nerve repair is usually performed within the first 6 months of life.
    </p>

    <h3>Consult a Specialist</h3>
    <p>
      If you are experiencing symptoms of brachial plexus injury, consult Dr. R. Suneel 
      for expert diagnosis and advanced surgical care in Hyderabad.
    </p>

  </div>
</section>

      {/* CONDITIONS GRID */}
      <section className="hs-section hs-conditions">
        <div className="hs-container">
          <div className="hs-section-head">
            <div className="hs-section-tag">Conditions We Treat</div>
            <h2 className="hs-section-title">
              Comprehensive <em>hand & wrist</em> treatment
            </h2>
            <div className="hs-divider" />
            <p className="hs-section-sub">
              From traumatic injuries to congenital conditions, we offer surgical
              expertise across the full spectrum of wrist disorders.
            </p>
          </div>

          <div className="hs-grid">
            {CONDITIONS.map((c, i) => (
              <article className="hs-card" key={i}>
                <div className="hs-card-num">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="hs-card-title">{c.title}</h3>
                <p className="hs-card-desc">{c.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* SMALL TEXT / CLOSING NOTE */}
      <section className="hs-section hs-note">
        <div className="hs-container hs-note-inner">
          <p className="hs-note-text">
            Every hand is unique — and so is every treatment plan. Our team
            combines microsurgical precision with years of specialist experience to
            deliver personalized care tailored to your recovery goals.
          </p>
        </div>
      </section>

      {/* IMAGE GALLERY */}
      <section className="hs-section hs-gallery-section">
        <div className="hs-container">
          <div className="hs-section-head hs-center">
            <div className="hs-section-tag">Case Gallery</div>
            <h2 className="hs-section-title">
              Results from our <em>Brachial surgery</em> cases
            </h2>
            <div className="hs-divider hs-center-divider" />
          </div>

          <div className="hs-gallery">
            {GALLERY.map((img, i) => (
              <figure className="hs-gallery-item" key={i}>
                <img src={img.src} alt={img.alt} loading="lazy" />
                <figcaption className="hs-gallery-cap">
                  Case {String(i + 1).padStart(2, "0")}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      
    </div>
  );
}